/** Dashboard entry point — dashboard.recued.com
 *
 *  Authenticated SPA for publisher management, account settings, billing,
 *  and server-binding status. Uses hash routing (#/home, #/recipes, etc.).
 */

import { createAuthClient, AuthRequestError, type AuthClient } from './auth.js';
import type { AccountBindingAuditEntry, AccountBindingServersResponse, AccountRotateCredentialResult, AccountUnbindResult } from '@recued/contracts';
import {
  renderDashboard, initialState, defaultProConveniences, pendingAccountBindingRead,
  type DashboardState, type Page, type AuthMode, type PublishedItem, type SubmissionItem,
  type DashboardPlan, type BindingStatus, type BillingStatus, type AccountBindingReadModel,
  type PublishSource, type RecipeSort, type RecipeStatusFilter,
  type PublishValidationContext, markSubmissionCancelled, validatePublishDraft,
  type BillingTransactionView,
} from './template.js';

export const ACCOUNT_BINDING_SERVERS_PATH = '/v1/account/binding/servers';
export const ACCOUNT_BINDING_UNBIND_PATH = '/v1/account/binding/unbind';
export const ACCOUNT_BINDING_ROTATE_PATH = '/v1/account/binding/rotate';
export const DASHBOARD_PUBLISH_PATH = '/v1/dashboard/publish';
export const DASHBOARD_BILLING_CHECKOUT_PATH = '/v1/dashboard/billing/checkout';
export const DASHBOARD_BILLING_CANCEL_PATH = '/v1/dashboard/billing/cancel';
export const DASHBOARD_BILLING_RESUME_PATH = '/v1/dashboard/billing/resume';
export const DASHBOARD_BILLING_PORTAL_PATH = '/v1/dashboard/billing/portal';
export const DASHBOARD_BILLING_HISTORY_PATH = '/v1/dashboard/billing/transactions';
export const DASHBOARD_BILLING_INVOICE_PATH = '/v1/dashboard/billing/invoice';

export const resolveAuthWorkerUrl = (host = typeof window !== 'undefined' ? window.location.hostname : ''): string => {
  if (host.endsWith('.recued2.com')) return 'https://auth.recued2.com';
  return 'https://auth.recued.com';
};

// ────────────────────────────────────────────────────────────────
// State
// ────────────────────────────────────────────────────────────────

let state: DashboardState = initialState();
let root: HTMLElement | null = null;
let auth: AuthClient | null = null;

const setState = (partial: Partial<DashboardState>): void => {
  state = { ...state, ...partial };
  render();
};

const render = (): void => {
  if (!root) return;
  root.innerHTML = renderDashboard(state);
  attachHandlers();
};

// Confirm-email resend cooldown — the client-side timeout gate. One live
// countdown interval at a time; cleared when the confirm-email card is left.
const RESEND_COOLDOWN_S = 30;
let resendTimer: ReturnType<typeof setInterval> | null = null;

const clearResendCooldown = (): void => {
  if (resendTimer !== null) {
    clearInterval(resendTimer);
    resendTimer = null;
  }
};

const startResendCooldown = (): void => {
  clearResendCooldown();
  setState({ resendCooldownRemaining: RESEND_COOLDOWN_S });
  resendTimer = setInterval(() => {
    const next = state.resendCooldownRemaining - 1;
    if (next <= 0) {
      clearResendCooldown();
      setState({ resendCooldownRemaining: 0 });
    } else {
      setState({ resendCooldownRemaining: next });
    }
  }, 1000);
};

// ────────────────────────────────────────────────────────────────
// Routing
// ────────────────────────────────────────────────────────────────

const PAGES = new Set<Page>(['home', 'recipes', 'packs', 'publish', 'account']);

/** Route patch for the current hash. The retired Submissions page aliases to
 *  the publish page's In-review tab so old links and in-app CTAs keep landing
 *  somewhere honest. */
const routePatch = (): Partial<DashboardState> => {
  const hash = window.location.hash.replace(/^#\/?/, '');
  if (hash === 'submissions') return { page: 'publish', publishTab: 'review' };
  return { page: PAGES.has(hash as Page) ? (hash as Page) : 'home' };
};

const handleRoute = (): void => {
  // Handle signup deep link — switch to signup mode on the auth page
  const hash = window.location.hash.replace(/^#\/?/, '');
  if (!state.user && hash === 'signup') {
    setState({ authMode: 'signup' });
    return;
  }
  if (!state.user) return;
  setState(routePatch());
};

// ────────────────────────────────────────────────────────────────
// Data fetching
// ────────────────────────────────────────────────────────────────

interface DashboardItemsResponse {
  recipes: PublishedItem[];
  packs?: PublishedItem[];
}

interface DashboardMetadata {
  publisherHandle: string | null;
  publisherWebsite: string | null;
  publisherBio: string | null;
  plan: DashboardPlan;
  bindingStatus: BindingStatus;
  billingStatus: BillingStatus;
  billingRenewalLabel: string | null;
  cancelScheduled: boolean;
  subscriptionManageable: boolean;
}

/** The billing fields shared by the publisher GET and the cancel/resume
 *  responses — normalized into the dashboard's billing state in one place. */
interface DashboardBillingFields {
  plan: DashboardPlan;
  billingStatus: BillingStatus;
  billingRenewalLabel: string | null;
  cancelScheduled: boolean;
  subscriptionManageable: boolean;
}

interface DashboardMetadataResponse {
  publisherHandle?: string | null;
  publisher_handle?: string | null;
  publisherWebsite?: string | null;
  publisherBio?: string | null;
  plan?: unknown;
  subscriptionPlan?: unknown;
  subscription_plan?: unknown;
  tier?: unknown;
  bindingStatus?: unknown;
  binding_status?: unknown;
  serverBindingStatus?: unknown;
  server_binding_status?: unknown;
  billingStatus?: unknown;
  billing_status?: unknown;
  subscriptionStatus?: unknown;
  subscription_status?: unknown;
  renewsAt?: unknown;
  renews_at?: unknown;
  cancelsAt?: unknown;
  cancels_at?: unknown;
  currentPeriodEnd?: unknown;
  current_period_end?: unknown;
  cancelScheduled?: unknown;
  cancel_scheduled?: unknown;
  subscriptionManageable?: unknown;
  subscription_manageable?: unknown;
}

export interface DashboardBillingCheckoutResponse {
  checkout_url?: unknown;
  checkoutUrl?: unknown;
  transaction_id?: unknown;
}

const normalizePlan = (value: unknown): DashboardPlan => {
  if (typeof value === 'string' && value.toLowerCase() === 'pro') return 'Pro';
  return 'Free';
};

const normalizeBindingStatus = (value: unknown): BindingStatus => {
  if (typeof value !== 'string') return 'No server';
  const normalized = value.toLowerCase().replace(/[\s-]+/g, '_');
  if (normalized === 'bound') return 'Bound';
  if (normalized === 'conflict') return 'Conflict';
  return 'No server';
};

const normalizeBillingStatus = (value: unknown, plan: DashboardPlan): BillingStatus => {
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().replace(/[\s-]+/g, '_');
    if (normalized === 'active' || normalized === 'trialing' || normalized === 'paid') return 'Active';
    if (normalized === 'past_due' || normalized === 'payment_failed') return 'Past due';
    if (normalized === 'canceled' || normalized === 'cancelled') return 'Canceled';
    if (normalized === 'free') return 'Free';
    if (normalized === 'not_configured' || normalized === 'unknown') return 'Not configured';
  }
  return plan === 'Pro' ? 'Active' : 'Free';
};

const normalizeDateLabel = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

/** Normalize the billing slice (snake_case or camelCase, from the publisher GET
 *  or a cancel/resume response) into dashboard billing state. When a cancel is
 *  scheduled, the meaningful date is the cancellation date, not the renewal. */
export const normalizeBillingFields = (data: DashboardMetadataResponse): DashboardBillingFields => {
  const plan = normalizePlan(data.plan ?? data.subscriptionPlan ?? data.subscription_plan ?? data.tier);
  const cancelScheduled = (data.cancelScheduled ?? data.cancel_scheduled) === true;
  const billingStatus = normalizeBillingStatus(
    data.billingStatus ?? data.billing_status ?? data.subscriptionStatus ?? data.subscription_status,
    plan,
  );
  const renewLabel = normalizeDateLabel(
    data.renewsAt ?? data.renews_at ?? data.currentPeriodEnd ?? data.current_period_end,
  );
  const cancelLabel = normalizeDateLabel(data.cancelsAt ?? data.cancels_at);
  return {
    plan,
    billingStatus,
    billingRenewalLabel: cancelScheduled ? cancelLabel : renewLabel,
    cancelScheduled,
    subscriptionManageable: (data.subscriptionManageable ?? data.subscription_manageable) === true,
  };
};

export const mapAccountBindingAudit = (entry: AccountBindingAuditEntry): AccountBindingReadModel['auditEvents'][number] => ({
  action: entry.action,
  account_id: entry.account_id,
  server_fingerprint: entry.server_fingerprint,
  timestamp: entry.at,
  actor_surface: entry.action === 'account_bind_exchange_failed' ? 'auth_worker_exchange' : 'auth_worker',
  outcome: entry.reason ?? entry.action,
  confirmed: entry.action === 'account_rebind' || entry.action === 'account_unbind',
});

export const shouldUsePendingAccountBindingFallback = (err: unknown): boolean => {
  const message = err instanceof Error ? err.message : String(err);
  return /route not found|not_found|not found|not configured|binding is not configured/i.test(message);
};

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : String(err || fallback);

const publishValidationContext = (): PublishValidationContext => ({
  submissions: state.submissions,
  myRecipes: state.myRecipes,
  myPacks: state.myPacks,
});

export const publishFailurePatch = (err: unknown): Pick<DashboardState, 'publishStatus' | 'publishMessage'> => ({
  publishStatus: 'error',
  publishMessage: `${errorMessage(err, 'Publish failed.')} Retry when ready; the Kitchen handoff payload is still loaded.`,
});

export const checkoutRedirectUrl = (data: DashboardBillingCheckoutResponse): string => {
  const raw = data.checkout_url ?? data.checkoutUrl;
  if (typeof raw !== 'string') throw new Error('Checkout did not return a redirect URL.');
  try {
    const url = new URL(raw);
    if (url.protocol === 'https:') return url.toString();
  } catch {
    /* fall through */
  }
  throw new Error('Checkout returned an unsafe redirect URL.');
};

export interface DashboardBillingPortalResponse {
  portal_url?: unknown;
  portalUrl?: unknown;
  overview_url?: unknown;
  overviewUrl?: unknown;
}

const safePortalUrl = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  try {
    const url = new URL(raw);
    if (url.protocol === 'https:') return url.toString();
  } catch {
    /* fall through */
  }
  return null;
};

/** The dunning target — deep-links into the card form, because its only caller
 *  is the past-due "Update payment method" button. */
export const portalRedirectUrl = (data: DashboardBillingPortalResponse): string => {
  const raw = data.portal_url ?? data.portalUrl;
  if (typeof raw !== 'string') throw new Error('The billing portal did not return a link.');
  const safe = safePortalUrl(raw);
  if (safe) return safe;
  throw new Error('The billing portal returned an unsafe link.');
};

/** The "Manage billing" target — the portal's landing page, from which payment
 *  method, invoices, and cancel are all reachable. Paddle may omit the overview
 *  link, so this falls back to the dunning deep link rather than failing: a card
 *  form is a worse landing page than the overview, but it is still the customer's
 *  own portal, and the alternative is a dead button. */
export const portalOverviewRedirectUrl = (data: DashboardBillingPortalResponse): string => {
  const overview = safePortalUrl(data.overview_url ?? data.overviewUrl);
  if (overview) return overview;
  return portalRedirectUrl(data);
};

export interface DashboardBillingHistoryResponse {
  transactions?: unknown;
  next_cursor?: unknown;
  nextCursor?: unknown;
}

/** The next-page cursor from a history response, or null when there are no more
 *  pages. Treated as an opaque token — echoed back, never interpreted. */
export const parseBillingNextCursor = (data: DashboardBillingHistoryResponse): string | null => {
  const raw = data.next_cursor ?? data.nextCursor;
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
};

const optStringField = (row: Record<string, unknown>, field: string): string | null => {
  const value = row[field];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

/** Parse the billing-history response into view rows, defensively — anything
 *  without a usable `occurred_at` is dropped rather than rendered blank. */
export const parseBillingHistory = (data: DashboardBillingHistoryResponse): BillingTransactionView[] => {
  const raw = Array.isArray(data.transactions) ? data.transactions : [];
  const out: BillingTransactionView[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const occurredAt = optStringField(row, 'occurred_at');
    if (!occurredAt) continue;
    out.push({
      status: optStringField(row, 'status') ?? 'unknown',
      occurredAt,
      amountMinor: optStringField(row, 'amount_minor'),
      currencyCode: optStringField(row, 'currency_code'),
      invoiceNumber: optStringField(row, 'invoice_number'),
    });
  }
  return out;
};

export interface DashboardBillingInvoiceResponse {
  invoice_url?: unknown;
  invoiceUrl?: unknown;
}

export const invoiceDownloadUrl = (data: DashboardBillingInvoiceResponse): string => {
  const raw = data.invoice_url ?? data.invoiceUrl;
  if (typeof raw !== 'string') throw new Error('No invoice link was returned.');
  try {
    const url = new URL(raw);
    if (url.protocol === 'https:') return url.toString();
  } catch {
    /* fall through */
  }
  throw new Error('The invoice link was unsafe.');
};

const isAlreadyCancelledError = (err: unknown): boolean =>
  /already (cancelled|canceled)|submission is (cancelled|canceled)/i.test(errorMessage(err, ''));

const fetchAccountBindingRead = async (): Promise<AccountBindingReadModel> => {
  if (!auth) return pendingAccountBindingRead();
  try {
    const data = await auth.request<AccountBindingServersResponse>(ACCOUNT_BINDING_SERVERS_PATH);
    return {
      status: 'ready',
      bindings: data.servers,
      auditEvents: data.audit.map(mapAccountBindingAudit),
      error: null,
    };
  } catch (err) {
    if (shouldUsePendingAccountBindingFallback(err)) return pendingAccountBindingRead();
    return {
      status: 'error',
      bindings: [],
      auditEvents: [],
      error: errorMessage(err, 'Could not load server bindings.'),
    };
  }
};

const fetchDashboardMetadata = async (): Promise<DashboardMetadata> => {
  const fallback: DashboardMetadata = {
    publisherHandle: null,
    publisherWebsite: null,
    publisherBio: null,
    plan: 'Free',
    bindingStatus: 'No server',
    billingStatus: 'Free',
    billingRenewalLabel: null,
    cancelScheduled: false,
    subscriptionManageable: false,
  };
  if (!auth) return fallback;
  try {
    const data = await auth.request<DashboardMetadataResponse>('/v1/dashboard/publisher');
    const publisherHandle = data.publisherHandle ?? data.publisher_handle ?? null;
    const publisherWebsite = data.publisherWebsite ?? null;
    const publisherBio = data.publisherBio ?? null;
    const bindingStatus = normalizeBindingStatus(
      data.bindingStatus ?? data.binding_status ?? data.serverBindingStatus ?? data.server_binding_status,
    );
    const billing = normalizeBillingFields(data);
    return {
      publisherHandle,
      publisherWebsite,
      publisherBio,
      bindingStatus,
      ...billing,
    };
  } catch {
    return fallback;
  }
};

const fetchMyItems = async (publisherHandle: string): Promise<void> => {
  if (!auth) return;
  setState({ loading: true });
  try {
    const data = await auth.request<DashboardItemsResponse>(
      `/v1/dashboard/items?publisher=${encodeURIComponent(publisherHandle)}`,
    );
    setState({
      myRecipes: data.recipes,
      myPacks: data.packs ?? [],
      loading: false,
    });
  } catch (err) {
    setState({ loading: false, error: err instanceof Error ? err.message : String(err) });
  }
};

export const ITEM_STATUS_PATH = '/v1/dashboard/items/status';

/** Unpublish / republish one published artifact. Optimistic-free: the
 *  row's button renders busy until the PATCH lands, then the item lists
 *  refetch so status chips + marketplace visibility read from truth. */
const handleSetItemStatus = async (
  type: 'recipe' | 'pack',
  id: string,
  status: 'published' | 'unpublished',
): Promise<void> => {
  if (!auth || !state.publisherHandle) return;
  // One mutation at a time — a second toggle while a PATCH is in
  // flight would clobber the single busy key and let the slower
  // response clear the faster row's feedback.
  if (state.itemStatusUpdatingKey !== null) return;
  const key = `${type}:${id}`;
  const nextErrors = { ...state.itemStatusErrors };
  delete nextErrors[key];
  setState({ itemStatusUpdatingKey: key, itemStatusErrors: nextErrors });
  try {
    await auth.request(ITEM_STATUS_PATH, {
      method: 'PATCH',
      body: JSON.stringify({ type, id, status }),
    });
    setState({ itemStatusUpdatingKey: null });
    await fetchMyItems(state.publisherHandle);
  } catch (err) {
    setState({
      itemStatusUpdatingKey: null,
      itemStatusErrors: {
        ...state.itemStatusErrors,
        [key]: errorMessage(err, 'Status change failed.'),
      },
    });
  }
};

const isItemType = (value: unknown): value is 'recipe' | 'pack' =>
  value === 'recipe' || value === 'pack';

export const ITEM_META_PATH = '/v1/dashboard/items/meta';

/** Save the metadata editor — sends ONLY changed fields (the initial
 *  repo can be unknown client-side; sending an unchanged blank would
 *  silently clear a stored value). Server sanitizes at the storage
 *  boundary; the client pre-trims + validates for fast feedback. */
const handleSaveItemMeta = async (): Promise<void> => {
  if (!auth || !state.publisherHandle || !state.editingItemKey || state.metaSaving) return;
  const sep = state.editingItemKey.indexOf(':');
  const type = state.editingItemKey.slice(0, sep);
  const id = state.editingItemKey.slice(sep + 1);
  if ((type !== 'recipe' && type !== 'pack') || !id) return;

  const repoTrimmed = state.editRepoInput.trim();
  const descTrimmed = state.editDescriptionInput.trim();
  const repoChanged = repoTrimmed !== state.editInitialRepo.trim();
  const descChanged = descTrimmed !== state.editInitialDescription.trim();
  if (!repoChanged && !descChanged) {
    setState({ editingItemKey: null, metaError: null });
    return;
  }
  if (repoChanged && repoTrimmed !== '' && !repoTrimmed.startsWith('https://')) {
    setState({ metaError: 'Support repo must be an https:// URL (or empty to clear).' });
    return;
  }
  if (descChanged && descTrimmed === '') {
    setState({ metaError: 'Description must not be empty.' });
    return;
  }
  setState({ metaSaving: true, metaError: null });
  try {
    await auth.request(ITEM_META_PATH, {
      method: 'PATCH',
      body: JSON.stringify({
        type,
        id,
        ...(repoChanged ? { repo: repoTrimmed === '' ? null : repoTrimmed } : {}),
        ...(descChanged ? { description: descTrimmed } : {}),
      }),
    });
    setState({ editingItemKey: null, metaSaving: false, metaError: null });
    await fetchMyItems(state.publisherHandle);
  } catch (err) {
    setState({ metaSaving: false, metaError: errorMessage(err, 'Saving details failed.') });
  }
};

const fetchSubmissions = async (): Promise<void> => {
  if (!auth) return;
  try {
    const data = await auth.request<{ submissions: SubmissionItem[] }>('/v1/dashboard/submissions');
    setState({ submissions: data.submissions, error: null });
  } catch (err) {
    setState({
      error: err instanceof Error ? err.message : String(err),
    });
  }
};

const handleCancelSubmission = async (reviewId: string): Promise<void> => {
  if (!auth) return;
  const nextErrors = { ...state.submissionActionErrors };
  delete nextErrors[reviewId];
  setState({ submissionCancellingId: reviewId, submissionActionErrors: nextErrors });
  const applyCancelled = (): void => {
    setState({
      submissions: markSubmissionCancelled(state.submissions, reviewId),
      submissionCancellingId: null,
      submissionActionErrors: nextErrors,
    });
  };
  try {
    await auth.request(`/v1/dashboard/submissions/${encodeURIComponent(reviewId)}/cancel`, {
      method: 'PATCH',
    });
    applyCancelled();
    await fetchSubmissions();
  } catch (err) {
    if (isAlreadyCancelledError(err)) {
      applyCancelled();
      return;
    }
    setState({
      submissionCancellingId: null,
      submissionActionErrors: {
        ...state.submissionActionErrors,
        [reviewId]: err instanceof Error ? err.message : String(err),
      },
    });
  }
};

/** How recently an `account_bind_conflict` audit row stays chip-worthy. A
 *  conflict is a transient bind-time outcome (the rebind is confirmed in the
 *  webclient, which holds the pair channel) — so the dashboard chip surfaces it
 *  only as a recent-activity signal, then falls back to the durable Bound /
 *  No server truth. The conflict row itself stays visible in the audit feed. */
const RECENT_BINDING_CONFLICT_WINDOW_MS = 24 * 60 * 60 * 1000;

/** True when the newest binding-audit event is a still-recent takeover attempt
 *  (`account_bind_conflict`). A later confirmed rebind / unbind emits a newer
 *  row, so "newest event is a conflict" means it has not been superseded. */
export const hasRecentBindingConflict = (read: AccountBindingReadModel, now: number): boolean => {
  if (read.status !== 'ready' || read.auditEvents.length === 0) return false;
  const latest = read.auditEvents[0]; // Worker returns audit rows newest-first.
  return latest.action === 'account_bind_conflict'
    && now - latest.timestamp <= RECENT_BINDING_CONFLICT_WINDOW_MS;
};

/** Reconcile the coarse binding chip from the authoritative servers-list read.
 *  The publisher GET never returns a binding status, so the chip MUST be
 *  computed here (DB-4) — at sign-in / refresh / unbind / rotate — or it sticks
 *  at the `No server` fallback even for a bound account. A recent conflict wins
 *  over Bound: it is the actionable signal to surface. */
export const bindingStatusFromRead = (
  read: AccountBindingReadModel,
  fallback: BindingStatus,
  now: number = Date.now(),
): BindingStatus => {
  if (read.status !== 'ready') return fallback;
  if (hasRecentBindingConflict(read, now)) return 'Conflict';
  return read.bindings.length > 0 ? 'Bound' : 'No server';
};

const handleUnbindServer = async (serverFingerprint: string): Promise<void> => {
  if (!auth || !serverFingerprint) return;
  const nextMessages = { ...state.bindingActionMessages };
  delete nextMessages[serverFingerprint];
  setState({ bindingUnbindingId: serverFingerprint, bindingActionMessages: nextMessages });
  try {
    const result = await auth.request<AccountUnbindResult>(ACCOUNT_BINDING_UNBIND_PATH, {
      method: 'POST',
      body: JSON.stringify({ server_fingerprint: serverFingerprint }),
    });
    setState({
      bindingUnbindingId: null,
      bindingActionMessages: {
        ...state.bindingActionMessages,
        [serverFingerprint]: {
          kind: 'success',
          message: result.outcome === 'unbound' ? 'Server unbound.' : 'Server was not bound.',
        },
      },
    });
    const accountBindingRead = await fetchAccountBindingRead();
    const bindingStatus = bindingStatusFromRead(accountBindingRead, state.bindingStatus);
    setState({
      accountBindingRead,
      bindingStatus,
      proConveniences: defaultProConveniences(state.plan, bindingStatus, state.publisherHandle),
    });
  } catch (err) {
    setState({
      bindingUnbindingId: null,
      bindingActionMessages: {
        ...state.bindingActionMessages,
        [serverFingerprint]: {
          kind: 'error',
          message: errorMessage(err, 'Could not unbind server.'),
        },
      },
    });
  }
};

const handleRotateServerCredential = async (serverFingerprint: string): Promise<void> => {
  if (!auth || !serverFingerprint) return;
  const nextMessages = { ...state.bindingActionMessages };
  delete nextMessages[serverFingerprint];
  setState({ bindingRotatingId: serverFingerprint, bindingActionMessages: nextMessages });
  try {
    const result = await auth.request<AccountRotateCredentialResult>(ACCOUNT_BINDING_ROTATE_PATH, {
      method: 'POST',
      body: JSON.stringify({ server_fingerprint: serverFingerprint }),
    });
    setState({
      bindingRotatingId: null,
      bindingActionMessages: {
        ...state.bindingActionMessages,
        [serverFingerprint]: {
          kind: 'success',
          message: result.outcome === 'rotated'
            ? 'Credential revoked. Re-pair this server to issue a fresh one.'
            : 'Server was not bound.',
        },
      },
    });
    const accountBindingRead = await fetchAccountBindingRead();
    const bindingStatus = bindingStatusFromRead(accountBindingRead, state.bindingStatus);
    setState({
      accountBindingRead,
      bindingStatus,
      proConveniences: defaultProConveniences(state.plan, bindingStatus, state.publisherHandle),
    });
  } catch (err) {
    setState({
      bindingRotatingId: null,
      bindingActionMessages: {
        ...state.bindingActionMessages,
        [serverFingerprint]: {
          kind: 'error',
          message: errorMessage(err, 'Could not rotate the credential.'),
        },
      },
    });
  }
};

/** GoTrue global logout — revokes every refresh token for the account.
 *  Other browsers' sessions die at their next refresh or live-validated
 *  call. This browser drops straight to the signed-out state. */
const handleSignOutAll = async (): Promise<void> => {
  if (!auth) return;
  setState({ signOutAllStatus: 'working', signOutAllMessage: null });
  try {
    await auth.request('/v1/auth/signout', {
      method: 'POST',
      body: JSON.stringify({ scope: 'global' }),
    });
    setState({ ...initialState(), page: 'signin', sessionStatus: 'unauthenticated' });
  } catch (err) {
    setState({
      signOutAllStatus: 'idle',
      signOutAllMessage: errorMessage(err, 'Could not sign out other sessions.'),
    });
  }
};

const handleStartProCheckout = async (): Promise<void> => {
  if (!auth) return;
  setState({ checkoutStatus: 'starting', checkoutMessage: null });
  try {
    const data = await auth.request<DashboardBillingCheckoutResponse>(DASHBOARD_BILLING_CHECKOUT_PATH, {
      method: 'POST',
    });
    const url = checkoutRedirectUrl(data);
    if (typeof window !== 'undefined') window.location.href = url;
  } catch (err) {
    setState({
      checkoutStatus: 'error',
      checkoutMessage: errorMessage(err, 'Could not start checkout.'),
    });
  }
};

/** Apply a cancel/resume billing response (the projection) onto state, keeping
 *  the Pro convenience strip in sync with the new plan. */
const applyBillingResponse = (data: DashboardMetadataResponse): void => {
  const billing = normalizeBillingFields(data);
  setState({
    plan: billing.plan,
    billingStatus: billing.billingStatus,
    billingRenewalLabel: billing.billingRenewalLabel,
    cancelScheduled: billing.cancelScheduled,
    subscriptionManageable: billing.subscriptionManageable,
    billingActionStatus: 'idle',
    billingActionMessage: null,
    billingCancelConfirm: false,
    // Drop any loaded history — a re-open re-fetches against the new plan state.
    billingHistory: null,
    billingHistoryStatus: 'idle',
    billingHistoryMessage: null,
    billingHistoryCursor: null,
    billingHistoryNextCursor: null,
    billingHistoryPrevCursors: [],
    billingInvoiceIndex: null,
    billingInvoiceMessage: null,
    proConveniences: defaultProConveniences(billing.plan, state.bindingStatus, state.publisherHandle),
  });
};

/** Schedule a period-end cancellation of Pro (reversible via resume). */
const handleConfirmCancelSubscription = async (): Promise<void> => {
  if (!auth || state.billingActionStatus !== 'idle') return;
  setState({ billingActionStatus: 'cancelling', billingActionMessage: null });
  try {
    const data = await auth.request<DashboardMetadataResponse>(DASHBOARD_BILLING_CANCEL_PATH, {
      method: 'POST',
    });
    applyBillingResponse(data);
  } catch (err) {
    setState({
      billingActionStatus: 'idle',
      billingActionMessage: errorMessage(err, 'Could not schedule the cancellation.'),
    });
  }
};

/** Remove a scheduled period-end cancellation. */
const handleResumeProSubscription = async (): Promise<void> => {
  if (!auth || state.billingActionStatus !== 'idle') return;
  setState({ billingActionStatus: 'resuming', billingActionMessage: null });
  try {
    const data = await auth.request<DashboardMetadataResponse>(DASHBOARD_BILLING_RESUME_PATH, {
      method: 'POST',
    });
    applyBillingResponse(data);
  } catch (err) {
    setState({
      billingActionStatus: 'idle',
      billingActionMessage: errorMessage(err, 'Could not resume the subscription.'),
    });
  }
};

/** Past-due dunning recovery — mint a Paddle customer-portal session and
 *  redirect to its update-payment-method deep link. On success the page
 *  navigates away, so the busy state is left set (it unloads). */
const handleUpdatePayment = async (): Promise<void> => {
  if (!auth || state.billingActionStatus !== 'idle') return;
  setState({ billingActionStatus: 'opening-portal', billingActionMessage: null });
  try {
    const data = await auth.request<DashboardBillingPortalResponse>(DASHBOARD_BILLING_PORTAL_PATH, {
      method: 'POST',
    });
    const url = portalRedirectUrl(data);
    if (typeof window !== 'undefined') window.location.href = url;
  } catch (err) {
    setState({
      billingActionStatus: 'idle',
      billingActionMessage: errorMessage(err, 'Could not open the billing portal.'),
    });
  }
};

/** "Manage billing" — mint a Paddle customer-portal session and redirect to its
 *  overview, where the customer can update payment, read invoices, and cancel.
 *
 *  This replaces three anchors that pointed at `https://billing.recued.com/portal`
 *  (2026-07-29). That hostname was never built: no app, no worker, no route in
 *  any wrangler config, NXDOMAIN on the authoritative nameserver. It rendered
 *  unconditionally for every signed-in user — including, as `btn-primary`, the
 *  Account page's own header — so the one control Paddle expects a subscriber to
 *  reach in order to cancel led nowhere, while a green test asserted the href.
 *  The session mint below always existed; only the buttons pointed elsewhere.
 *
 *  On success the page navigates away, so the busy state is left set (it unloads). */
const handleOpenBillingPortal = async (): Promise<void> => {
  if (!auth || state.billingActionStatus !== 'idle') return;
  setState({ billingActionStatus: 'opening-portal', billingActionMessage: null });
  try {
    const data = await auth.request<DashboardBillingPortalResponse>(DASHBOARD_BILLING_PORTAL_PATH, {
      method: 'POST',
    });
    const url = portalOverviewRedirectUrl(data);
    if (typeof window !== 'undefined') window.location.href = url;
  } catch (err) {
    setState({
      billingActionStatus: 'idle',
      billingActionMessage: errorMessage(err, 'Could not open the billing portal.'),
    });
  }
};

/** Load one page of past Pro charges. `after` is the opaque cursor for the page
 *  to fetch (`undefined` = first page); `prevCursors` is the back-stack to apply
 *  on success (so Next/Previous keep their place). Read-only — never mutates
 *  billing. */
const loadBillingHistoryPage = async (
  after: string | undefined,
  prevCursors: (string | null)[],
): Promise<void> => {
  if (!auth || state.billingHistoryStatus === 'loading') return;
  setState({ billingHistoryStatus: 'loading', billingHistoryMessage: null, billingInvoiceIndex: null, billingInvoiceMessage: null });
  try {
    const path = after ? `${DASHBOARD_BILLING_HISTORY_PATH}?after=${encodeURIComponent(after)}` : DASHBOARD_BILLING_HISTORY_PATH;
    const data = await auth.request<DashboardBillingHistoryResponse>(path);
    setState({
      billingHistory: parseBillingHistory(data),
      billingHistoryStatus: 'loaded',
      billingHistoryMessage: null,
      billingHistoryCursor: after ?? null,
      billingHistoryNextCursor: parseBillingNextCursor(data),
      billingHistoryPrevCursors: prevCursors,
    });
  } catch (err) {
    setState({
      billingHistoryStatus: 'error',
      billingHistoryMessage: errorMessage(err, 'Could not load billing history.'),
    });
  }
};

/** Lazily open the billing history (first page) / retry. Resets the pager. */
const handleLoadBillingHistory = (): Promise<void> => loadBillingHistoryPage(undefined, []);

/** Next page — remember the current page's cursor so Previous can return. */
const handleBillingHistoryNext = (): Promise<void> => {
  if (state.billingHistoryNextCursor === null) return Promise.resolve();
  return loadBillingHistoryPage(state.billingHistoryNextCursor, [...state.billingHistoryPrevCursors, state.billingHistoryCursor]);
};

/** Previous page — pop the back-stack and re-fetch that cursor's page. */
const handleBillingHistoryPrev = (): Promise<void> => {
  if (state.billingHistoryPrevCursors.length === 0) return Promise.resolve();
  const prev = [...state.billingHistoryPrevCursors];
  const target = prev.pop() ?? null;
  return loadBillingHistoryPage(target ?? undefined, prev);
};

/** Mint + open a short-lived Paddle invoice-PDF link for one billed charge.
 *  The server resolves the charge by its INDEX in the history list (no Paddle
 *  id crosses the wire). One download at a time. */
const handleDownloadInvoice = async (index: number): Promise<void> => {
  if (!auth || state.billingInvoiceIndex !== null || !Number.isInteger(index) || index < 0) return;
  const row = state.billingHistory?.[index];
  if (!row) return;
  setState({ billingInvoiceIndex: index, billingInvoiceMessage: null });
  try {
    const data = await auth.request<DashboardBillingInvoiceResponse>(DASHBOARD_BILLING_INVOICE_PATH, {
      method: 'POST',
      // Send the displayed date + amount so the server can confirm the index
      // still points at the row the user clicked (guards a list-order shift),
      // plus the current page's cursor so the server re-fetches the SAME page.
      body: JSON.stringify({
        index,
        occurred_at: row.occurredAt,
        amount_minor: row.amountMinor,
        invoice_number: row.invoiceNumber,
        currency_code: row.currencyCode,
        ...(state.billingHistoryCursor !== null ? { after: state.billingHistoryCursor } : {}),
      }),
    });
    const url = invoiceDownloadUrl(data);
    // Paddle serves the PDF with Content-Disposition: attachment, so opening the
    // link downloads it while leaving the dashboard tab in place.
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener');
    setState({ billingInvoiceIndex: null, billingInvoiceMessage: null });
  } catch (err) {
    setState({
      billingInvoiceIndex: null,
      billingInvoiceMessage: errorMessage(err, 'Could not download the invoice.'),
    });
  }
};

// ────────────────────────────────────────────────────────────────
// Event handlers
// ────────────────────────────────────────────────────────────────

const attachHandlers = (): void => {
  if (!root) return;

  // Field inputs
  const fields = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-field]');
  for (const field of fields) {
    const syncField = (): void => {
      const name = field.dataset.field;
      if (name === 'email') state = { ...state, authEmail: field.value, authError: null, resetSuccess: null };
      else if (name === 'password') state = { ...state, authPassword: field.value, authError: null };
      else if (name === 'new-password') state = { ...state, authNewPassword: field.value, authError: null };
      else if (name === 'agree-terms') state = { ...state, authAgreeTerms: (field as HTMLInputElement).checked, authError: null };
      else if (name === 'publisher-handle') state = { ...state, publisherInput: field.value };
      else if (name === 'delete-account-confirm') {
        setState({ deleteAccountConfirm: field.value, deleteAccountMessage: null });
        return;
      }
      else if (name === 'edit-repo') state = { ...state, editRepoInput: field.value, metaError: null };
      else if (name === 'edit-description') state = { ...state, editDescriptionInput: field.value, metaError: null };
      else if (name === 'edit-website') state = { ...state, profileWebsiteInput: field.value, profileMessage: null };
      else if (name === 'edit-bio') state = { ...state, profileBioInput: field.value, profileMessage: null };
      else if (name === 'handle-rename') state = { ...state, handleRenameInput: field.value, handleRenameMessage: null };
      else if (name === 'publish-json') state = { ...state, publishJson: field.value, publishSource: 'paste', publishStatus: 'idle', publishMessage: null };
      else if (name === 'recipe-search') {
        setState({ recipeSearch: field.value, selectedRecipeKey: null });
        return;
      } else if (name === 'recipe-status-filter') {
        setState({ recipeStatusFilter: (field.value as RecipeStatusFilter) || 'all', selectedRecipeKey: null });
        return;
      } else if (name === 'recipe-sort') {
        setState({ recipeSort: (field.value as RecipeSort) || 'updated-desc', selectedRecipeKey: null });
        return;
      }
      // Light update for buttons only
      updateDisabledStates();
    };
    field.addEventListener('input', syncField);
    field.addEventListener('change', syncField);
  }

  // The publish textarea keeps the light no-render sync while typing (caret
  // safety), but the validation cards must react when an artifact lands: a
  // paste or a blur-with-changes triggers a full re-render.
  const publishJsonFields = root.querySelectorAll<HTMLTextAreaElement>('textarea[data-field="publish-json"]');
  for (const field of publishJsonFields) {
    const commit = (): void => setState({
      publishJson: field.value, publishSource: 'paste', publishStatus: 'idle', publishMessage: null,
    });
    field.addEventListener('change', commit);
    field.addEventListener('paste', () => { setTimeout(commit, 0); });
  }

  const fileInputs = root.querySelectorAll<HTMLInputElement>('input[data-file-field="publish-json-file"]');
  for (const input of fileInputs) {
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        setState({ publishJson: text, publishSource: 'upload', publishStatus: 'idle', publishMessage: null });
      } catch (err) {
        setState({ publishStatus: 'error', publishMessage: err instanceof Error ? err.message : String(err) });
      }
    });
  }

  // Action buttons
  const buttons = root.querySelectorAll<HTMLButtonElement>('button[data-action]');
  for (const btn of buttons) {
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      handleAction(btn.dataset.action, btn.dataset);
    });
  }
};

const updateDisabledStates = (): void => {
  if (!root) return;
  const authBtn = root.querySelector<HTMLButtonElement>('[data-action="auth-submit"]');
  if (authBtn) authBtn.disabled = !state.authEmail || !state.authPassword || state.authLoading || (state.authMode === 'signup' && !state.authAgreeTerms);
  const pubHandleBtn = root.querySelector<HTMLButtonElement>('[data-action="create-publisher"]');
  if (pubHandleBtn) pubHandleBtn.disabled = !state.publisherInput.trim();
  const publishBtn = root.querySelector<HTMLButtonElement>('[data-action="publish"]');
  if (publishBtn) {
    publishBtn.disabled = state.publishStatus === 'publishing'
      || !validatePublishDraft(state.publishType, state.publishJson, state.publisherHandle, publishValidationContext()).canSubmit;
  }
};

const handleAction = (action: string | undefined, dataset: DOMStringMap): void => {
  switch (action) {
    case 'auth-mode':
      clearResendCooldown();
      setState({ authMode: (dataset.mode as AuthMode) ?? 'signin', authError: null, resetSuccess: null, resendStatus: 'idle', resendMessage: null, resendCooldownRemaining: 0 });
      break;
    case 'auth-submit':
      void handleAuthSubmit();
      break;
    case 'resend-confirmation':
      void handleResendConfirmation();
      break;
    case 'reset-request':
      void handleResetRequest();
      break;
    case 'reset-update':
      void handleResetUpdate();
      break;
    case 'oauth':
      void handleOAuth(dataset.provider as 'google' | 'github');
      break;
    case 'retry-session':
      void loadSession();
      break;
    case 'retry-page':
      void refreshCurrentPage();
      break;
    case 'sign-out':
      void handleSignOut();
      break;
    case 'create-publisher':
      void handleCreatePublisher();
      break;
    case 'publish-tab':
      setState({ publishTab: dataset.value === 'review' ? 'review' : 'publish' });
      break;
    case 'publish-type':
      setState({ publishType: (dataset.value as 'recipe' | 'pack') ?? 'recipe', publishStatus: 'idle', publishMessage: null });
      break;
    case 'publish-source':
      setState({ publishSource: (dataset.value as PublishSource) ?? 'paste', publishStatus: 'idle', publishMessage: null });
      break;
    case 'publish':
      void handlePublish();
      break;
    case 'publish-reset':
      // Fresh draft: source returns to the kitchen default; the type toggle
      // stays sticky (publishing several of the same kind in a row).
      setState({ publishStatus: 'idle', publishMessage: null, publishJson: '', publishSource: 'kitchen' });
      break;
    case 'select-recipe':
      if (dataset?.recipeKey) setState({ selectedRecipeKey: dataset.recipeKey });
      break;
    case 'clear-recipe-filters':
      setState({ recipeSearch: '', recipeStatusFilter: 'all', recipeSort: 'updated-desc', selectedRecipeKey: null });
      break;
    case 'copy-text':
      if (dataset?.copyValue) void copyText(dataset.copyValue, dataset.copyLabel ?? 'Copied');
      break;
    case 'cancel-submission':
      if (dataset?.reviewId) void handleCancelSubmission(dataset.reviewId);
      break;
    case 'unpublish-item':
      if (isItemType(dataset?.itemType) && dataset?.itemId) {
        void handleSetItemStatus(dataset.itemType, dataset.itemId, 'unpublished');
      }
      break;
    case 'republish-item':
      if (isItemType(dataset?.itemType) && dataset?.itemId) {
        void handleSetItemStatus(dataset.itemType, dataset.itemId, 'published');
      }
      break;
    case 'edit-item-meta':
      if ((dataset?.itemType === 'recipe' || dataset?.itemType === 'pack') && dataset?.itemId) {
        const repo = dataset.repo ?? '';
        const description = dataset.description ?? '';
        setState({
          editingItemKey: `${dataset.itemType}:${dataset.itemId}`,
          editRepoInput: repo,
          editDescriptionInput: description,
          editInitialRepo: repo,
          editInitialDescription: description,
          metaSaving: false,
          metaError: null,
        });
      }
      break;
    case 'cancel-item-meta':
      setState({ editingItemKey: null, metaSaving: false, metaError: null });
      break;
    case 'save-item-meta':
      void handleSaveItemMeta();
      break;
    case 'edit-profile':
      setState({
        profileEditing: true,
        profileWebsiteInput: state.publisherWebsite ?? '',
        profileBioInput: state.publisherBio ?? '',
        profileSaving: false,
        profileMessage: null,
      });
      break;
    case 'cancel-profile-edit':
      setState({ profileEditing: false, profileSaving: false, profileMessage: null });
      break;
    case 'save-publisher-profile':
      void handleSavePublisherProfile();
      break;
    case 'edit-handle':
      setState({
        handleRenameOpen: true,
        handleRenameInput: state.publisherHandle ?? '',
        handleRenameSaving: false,
        handleRenameMessage: null,
      });
      break;
    case 'cancel-handle':
      setState({ handleRenameOpen: false, handleRenameSaving: false, handleRenameMessage: null });
      break;
    case 'save-handle':
      void handleRenamePublisherHandle();
      break;
    case 'unbind-server':
      if (dataset?.serverFingerprint) void handleUnbindServer(dataset.serverFingerprint);
      break;
    case 'rotate-server-credential':
      if (dataset?.serverFingerprint) void handleRotateServerCredential(dataset.serverFingerprint);
      break;
    case 'sign-out-all':
      void handleSignOutAll();
      break;
    case 'start-pro-checkout':
      void handleStartProCheckout();
      break;
    case 'open-cancel-subscription':
      setState({ billingCancelConfirm: true, billingActionMessage: null });
      break;
    case 'dismiss-cancel-subscription':
      setState({ billingCancelConfirm: false });
      break;
    case 'dismiss-checkout-return':
      setState({ checkoutReturn: null });
      break;
    case 'confirm-cancel-subscription':
      void handleConfirmCancelSubscription();
      break;
    case 'resume-pro-subscription':
      void handleResumeProSubscription();
      break;
    case 'update-payment':
      void handleUpdatePayment();
      break;
    case 'open-billing-portal':
      void handleOpenBillingPortal();
      break;
    case 'load-billing-history':
      void handleLoadBillingHistory();
      break;
    case 'billing-history-next':
      void handleBillingHistoryNext();
      break;
    case 'billing-history-prev':
      void handleBillingHistoryPrev();
      break;
    case 'download-invoice': {
      const idx = Number(dataset?.index);
      if (Number.isInteger(idx)) void handleDownloadInvoice(idx);
      break;
    }
    case 'open-delete-account':
      setState({ deleteAccountOpen: true, deleteAccountConfirm: '', deleteAccountStatus: 'idle', deleteAccountMessage: null });
      break;
    case 'cancel-delete-account':
      setState({ deleteAccountOpen: false, deleteAccountConfirm: '', deleteAccountStatus: 'idle', deleteAccountMessage: null });
      break;
    case 'confirm-delete-account':
      void handleDeleteAccount();
      break;
  }
};

// Copy feedback renders as a transient toast; it dismisses itself so a
// stale "copied" note never lingers into the next interaction.
const COPY_TOAST_MS = 2400;
let copyToastTimer: ReturnType<typeof setTimeout> | null = null;

const copyText = async (value: string, label: string): Promise<void> => {
  try {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      throw new Error('Clipboard is unavailable in this browser.');
    }
    await navigator.clipboard.writeText(value);
    setState({ copyStatus: `${label} copied.`, error: null });
    if (copyToastTimer !== null) clearTimeout(copyToastTimer);
    copyToastTimer = setTimeout(() => {
      copyToastTimer = null;
      if (state.copyStatus !== null) setState({ copyStatus: null });
    }, COPY_TOAST_MS);
  } catch (err) {
    setState({ copyStatus: null, error: err instanceof Error ? err.message : String(err) });
  }
};

const handleAuthSubmit = async (): Promise<void> => {
  if (!auth) return;
  // Defense-in-depth: the submit button is disabled until the Terms checkbox is
  // checked in signup mode, but guard the handler too (keyboard / programmatic).
  if (state.authMode === 'signup' && !state.authAgreeTerms) {
    setState({ authError: 'Please agree to the Terms of Service and Privacy Policy to create an account.' });
    return;
  }
  setState({ authLoading: true, authError: null });
  try {
    const session = state.authMode === 'signup'
      ? await auth.signUp(state.authEmail, state.authPassword)
      : await auth.signIn(state.authEmail, state.authPassword);
    await onSignedIn(session.user);
  } catch (err) {
    // `no_session` = the account exists but email confirmation is pending. That
    // is a SUCCESS ("check your email"), not a failure — show the confirm-email
    // card instead of a red error flash (covers signup + unconfirmed sign-in).
    if (err instanceof AuthRequestError && err.code === 'no_session') {
      clearResendCooldown();
      setState({ authLoading: false, authMode: 'confirm-email', authError: null, resendStatus: 'idle', resendMessage: null, resendCooldownRemaining: 0 });
      return;
    }
    setState({ authLoading: false, authError: err instanceof Error ? err.message : String(err) });
  }
};

const handleResendConfirmation = async (): Promise<void> => {
  if (!auth || !state.authEmail || state.resendStatus === 'sending' || state.resendCooldownRemaining > 0) return;
  setState({ resendStatus: 'sending', resendMessage: null });
  try {
    await auth.resendConfirmation(state.authEmail);
    setState({ resendStatus: 'sent', resendMessage: 'Sent — check your inbox (and spam).' });
    startResendCooldown();
  } catch (err) {
    const rateLimited = err instanceof AuthRequestError && (err.code === 'resend_rate_limited' || err.status === 429);
    setState({
      resendStatus: 'error',
      resendMessage: rateLimited
        ? 'Please wait a moment before requesting another email.'
        : err instanceof Error ? err.message : 'Could not resend the confirmation email.',
    });
    // A rate-limit still starts the client cooldown so the button reflects the wait.
    if (rateLimited) startResendCooldown();
  }
};

const handleResetRequest = async (): Promise<void> => {
  if (!auth || !state.authEmail) return;
  setState({ authLoading: true, authError: null });
  try {
    await auth.requestPasswordReset(state.authEmail);
    setState({ authLoading: false, authMode: 'reset-sent', authError: null });
  } catch (err) {
    setState({ authLoading: false, authError: err instanceof Error ? err.message : String(err) });
  }
};

const handleResetUpdate = async (): Promise<void> => {
  if (!auth || !state.resetToken || !state.authNewPassword) return;
  if (state.authNewPassword.length < 6) {
    setState({ authError: 'Password must be at least 6 characters' });
    return;
  }
  setState({ authLoading: true, authError: null });
  try {
    const session = await auth.updatePassword(state.authNewPassword);
    await onSignedIn(session.user);
  } catch (err) {
    setState({ authLoading: false, authError: err instanceof Error ? err.message : String(err) });
  }
};

const handleOAuth = async (provider: 'google' | 'github'): Promise<void> => {
  if (!auth) return;
  await auth.signInWithOAuth(provider); // Navigates away
};

export const DASHBOARD_ACCOUNT_DELETE_PATH = '/v1/dashboard/account/delete';

/** Immediate, irreversible account deletion. The Worker re-validates the
 *  session live and re-checks the typed email server-side; client-side the
 *  confirm button is only enabled on an exact email match. On success the
 *  session cookie is already cleared by the response — drop straight to the
 *  signed-out state. */
const handleDeleteAccount = async (): Promise<void> => {
  if (!auth || !state.user?.email) return;
  const confirm = state.deleteAccountConfirm.trim();
  if (confirm.toLowerCase() !== state.user.email.trim().toLowerCase()) return;
  setState({ deleteAccountStatus: 'deleting', deleteAccountMessage: null });
  try {
    await auth.request(DASHBOARD_ACCOUNT_DELETE_PATH, {
      method: 'POST',
      body: JSON.stringify({ confirm_email: confirm }),
    });
    setState({
      ...initialState(),
      page: 'signin',
      sessionStatus: 'unauthenticated',
      resetSuccess: 'Your account has been permanently deleted.',
    });
  } catch (err) {
    setState({
      deleteAccountStatus: 'error',
      deleteAccountMessage: errorMessage(err, 'Account deletion failed — nothing was deleted.'),
    });
  }
};

const handleSignOut = async (): Promise<void> => {
  if (!auth) return;
  await auth.signOut();
  setState({ ...initialState(), page: 'signin', sessionStatus: 'unauthenticated' });
};

const handleCreatePublisher = async (): Promise<void> => {
  if (!auth) return;
  const handle = state.publisherInput.trim();
  if (!handle) return;
  try {
    const data = await auth.request<{ publisherHandle: string }>('/v1/dashboard/publisher', {
      method: 'POST',
      body: JSON.stringify({ handle }),
    });
    setState({
      publisherHandle: data.publisherHandle,
      proConveniences: defaultProConveniences(state.plan, state.bindingStatus, data.publisherHandle),
      error: null,
    });
    await fetchMyItems(data.publisherHandle);
  } catch (err) {
    setState({ error: err instanceof Error ? err.message : String(err) });
  }
};

export const PUBLISHER_PROFILE_PATH = '/v1/dashboard/publisher';

/** R20 — save the publisher profile (website + bio). The PATCH returns the
 *  canonical sanitized values, applied directly to state so the read view
 *  reflects exactly what was stored (no full metadata refetch). */
const handleSavePublisherProfile = async (): Promise<void> => {
  if (!auth || !state.publisherHandle || state.profileSaving) return;
  const website = state.profileWebsiteInput.trim();
  const bio = state.profileBioInput.trim();
  if (website !== '' && !website.startsWith('https://')) {
    setState({ profileMessage: 'Website must be an https:// URL (or empty to clear).' });
    return;
  }
  setState({ profileSaving: true, profileMessage: null });
  try {
    const data = await auth.request<{ publisherWebsite?: string | null; publisherBio?: string | null }>(
      PUBLISHER_PROFILE_PATH,
      { method: 'PATCH', body: JSON.stringify({ website: website || null, bio: bio || null }) },
    );
    setState({
      profileSaving: false,
      profileEditing: false,
      profileMessage: 'Profile saved.',
      publisherWebsite: data.publisherWebsite ?? null,
      publisherBio: data.publisherBio ?? null,
    });
  } catch (err) {
    setState({ profileSaving: false, profileMessage: errorMessage(err, 'Profile save failed.') });
  }
};

export const PUBLISHER_HANDLE_PATH = '/v1/dashboard/publisher/handle';

/** AW-2 — rename the marketplace publisher handle. The server re-keys the
 *  publisher's whole artifact tree (ON UPDATE CASCADE), so on success we adopt
 *  the new handle and refetch the published items (their `publisher_id` moved).
 *  Client-side checks only guard the obvious cases; the server is authoritative
 *  for format / reserved / availability. */
const handleRenamePublisherHandle = async (): Promise<void> => {
  if (!auth || !state.publisherHandle || state.handleRenameSaving) return;
  const next = state.handleRenameInput.trim().toLowerCase();
  if (!next || next === state.publisherHandle) return;
  setState({ handleRenameSaving: true, handleRenameMessage: null });
  try {
    const data = await auth.request<{ publisherHandle: string }>(
      PUBLISHER_HANDLE_PATH,
      { method: 'PATCH', body: JSON.stringify({ handle: next }) },
    );
    setState({
      handleRenameSaving: false,
      handleRenameOpen: false,
      handleRenameMessage: null,
      publisherHandle: data.publisherHandle,
      proConveniences: defaultProConveniences(state.plan, state.bindingStatus, data.publisherHandle),
    });
    await fetchMyItems(data.publisherHandle);
  } catch (err) {
    setState({ handleRenameSaving: false, handleRenameMessage: errorMessage(err, 'Handle change failed.') });
  }
};

const refreshCurrentPage = async (): Promise<void> => {
  if (!auth || !state.user) return;
  setState({ error: null });
  const metadata = await fetchDashboardMetadata();
  const accountBindingRead = await fetchAccountBindingRead();
  // DB-4 — reconcile the coarse binding chip from the authoritative
  // servers-list read. The publisher GET carries no binding status, so without
  // this the chip sticks at the `No server` fallback even for a bound account.
  metadata.bindingStatus = bindingStatusFromRead(accountBindingRead, metadata.bindingStatus);
  setState({
    publisherHandle: metadata.publisherHandle,
    publisherWebsite: metadata.publisherWebsite,
    publisherBio: metadata.publisherBio,
    plan: metadata.plan,
    bindingStatus: metadata.bindingStatus,
    billingStatus: metadata.billingStatus,
    billingRenewalLabel: metadata.billingRenewalLabel,
    cancelScheduled: metadata.cancelScheduled,
    subscriptionManageable: metadata.subscriptionManageable,
    checkoutStatus: 'idle',
    checkoutMessage: null,
    billingActionStatus: 'idle',
    billingActionMessage: null,
    billingCancelConfirm: false,
    proConveniences: defaultProConveniences(metadata.plan, metadata.bindingStatus, metadata.publisherHandle),
    accountBindingRead,
  });
  if (metadata.publisherHandle) {
    if (state.page === 'home' || state.page === 'recipes' || state.page === 'packs' || state.page === 'publish') {
      await fetchMyItems(metadata.publisherHandle);
    }
  }
  if (state.page === 'home' || state.page === 'recipes' || state.page === 'packs' || state.page === 'publish') {
    await fetchSubmissions();
  }
};

/** DB-7a — surface the outcome of a Paddle checkout / portal return. The auth
 *  client already stripped the `?checkout=` param; here we route to Account,
 *  refresh the plan in place (no full reload), then confirm. Pro activation is
 *  async (the Paddle webhook flips the flag), so success reads as "activating
 *  shortly" rather than asserting Pro is live.
 *
 *  Best-effort: the confirmation banner is skipped if the session lapsed during
 *  the (brief) checkout — a rare edge with no substantive loss, since the
 *  webhook activates Pro server-side regardless and the next sign-in's metadata
 *  fetch reflects the new plan. The banner is a confirmation toast, not the
 *  activation path. */
const handleBillingReturn = async (): Promise<void> => {
  if (!auth) return;
  const ret = auth.handleBillingReturn();
  if (!ret || !state.user) return;
  if (ret.checkout === 'success') {
    setState({ page: 'account' });
    await refreshCurrentPage();
    setState({ checkoutReturn: 'success' });
  } else {
    setState({ page: 'account', checkoutReturn: 'cancelled' });
  }
};

const handlePublish = async (): Promise<void> => {
  if (!auth || !state.publisherHandle) return;

  const validation = validatePublishDraft(state.publishType, state.publishJson, state.publisherHandle, publishValidationContext());
  if (!validation.canSubmit || !validation.parsed) {
    const blocking = validation.issues.find((issue) => issue.severity === 'error');
    setState({ publishStatus: 'error', publishMessage: blocking?.detail ?? 'Load a valid exported artifact before submitting.' });
    return;
  }

  setState({ publishStatus: 'publishing', publishMessage: null });
  try {
    await auth.request(DASHBOARD_PUBLISH_PATH, {
      method: 'POST',
      body: JSON.stringify({ type: state.publishType, manifest: validation.parsed, publisher_id: state.publisherHandle }),
    });
    setState({ publishStatus: 'success', publishMessage: `${state.publishType} published to the marketplace.`, publishJson: '' });
    await fetchMyItems(state.publisherHandle);
    await fetchSubmissions();
  } catch (err) {
    setState(publishFailurePatch(err));
  }
};

// ────────────────────────────────────────────────────────────────
// Bootstrap
// ────────────────────────────────────────────────────────────────

const onSignedIn = async (user: { id: string; email: string; created_at: string }): Promise<void> => {
  const metadata = await fetchDashboardMetadata();
  const accountBindingRead = await fetchAccountBindingRead();
  // DB-4 — reconcile the coarse binding chip from the authoritative
  // servers-list read. The publisher GET carries no binding status, so without
  // this the chip sticks at the `No server` fallback even for a bound account.
  metadata.bindingStatus = bindingStatusFromRead(accountBindingRead, metadata.bindingStatus);
  setState({
    user,
    sessionStatus: 'authenticated',
    sessionError: null,
    ...routePatch(),
    authLoading: false,
    authError: null,
    authPassword: '',
    publisherHandle: metadata.publisherHandle,
    publisherWebsite: metadata.publisherWebsite,
    publisherBio: metadata.publisherBio,
    plan: metadata.plan,
    bindingStatus: metadata.bindingStatus,
    billingStatus: metadata.billingStatus,
    billingRenewalLabel: metadata.billingRenewalLabel,
    cancelScheduled: metadata.cancelScheduled,
    subscriptionManageable: metadata.subscriptionManageable,
    checkoutStatus: 'idle',
    checkoutMessage: null,
    billingActionStatus: 'idle',
    billingActionMessage: null,
    billingCancelConfirm: false,
    proConveniences: defaultProConveniences(metadata.plan, metadata.bindingStatus, metadata.publisherHandle),
    accountBindingRead,
  });
  if (metadata.publisherHandle) await fetchMyItems(metadata.publisherHandle);
  void fetchSubmissions();

  // If the user arrived via a redirect from the extension (e.g.,
  // kitchen → signup → dashboard → signed in), surface the return link
  // so they can get back to their in-progress publish flow.
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get('return');
    if (returnUrl && returnUrl.startsWith('chrome-extension://')) {
      setState({ returnToExtensionUrl: returnUrl });
    }
  }
};

const loadSession = async (): Promise<void> => {
  if (!auth) return;
  setState({ sessionStatus: 'checking', sessionError: null, error: null });

  let session: Awaited<ReturnType<AuthClient['handleOAuthCallback']>> = null;
  try {
    session = await auth.handleOAuthCallback();
  } catch (err) {
    setState({
      page: 'signin',
      authMode: 'signin',
      sessionStatus: 'error',
      sessionError: err instanceof Error ? err.message : String(err),
    });
    return;
  }
  if (session) {
    await onSignedIn(session.user);
    return;
  }

  const hash = window.location.hash.replace(/^#\/?/, '');
  setState({ page: 'signin', sessionStatus: 'unauthenticated', authMode: hash === 'signup' ? 'signup' : 'signin' });
};

const init = async (): Promise<void> => {
  root = document.getElementById('root');
  if (!root) return;
  auth = createAuthClient(resolveAuthWorkerUrl());

  // Check for password recovery callback (magic link from email)
  const recovery = auth.handleRecoveryCallback();
  if (recovery) {
    setState({ page: 'signin', sessionStatus: 'unauthenticated', authMode: 'reset-update', resetToken: 'cookie-session' });
    window.addEventListener('hashchange', handleRoute);
    return;
  }

  // SW-8 — returning from an email-confirmation link. The account is confirmed
  // but has no session yet, so greet them on the sign-in form. Checked before
  // loadSession: the confirmation link establishes no session cookie to load.
  const emailConfirmed = auth.handleEmailConfirmedCallback();
  if (emailConfirmed) {
    setState({ page: 'signin', sessionStatus: 'unauthenticated', authMode: 'signin', emailConfirmedNotice: true });
    window.addEventListener('hashchange', handleRoute);
    return;
  }

  await loadSession();
  // DB-7a — returning from the Paddle checkout / customer portal. Only acts once
  // signed in (loadSession established the session); refreshes the plan in place.
  await handleBillingReturn();
  window.addEventListener('hashchange', handleRoute);
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void init());
  } else {
    void init();
  }
}
